import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { ShoppingCart, ArrowRight, X, Sparkles, BookOpen } from 'lucide-react';
import BookCover from './BookCover';
import { authFetch } from '../utils/session';
import { updateCartBadge } from '../utils/cart';
import { toast } from 'sonner';
import { API, BACKEND_URL } from '../utils/api';

const R2_URL = process.env.REACT_APP_R2_URL || BACKEND_URL + '/api/static';

const experienceOptions = [
  { value: 'amazing', emoji: '\u2728', label: 'Increíble' },
  { value: 'great', emoji: '\u{1F60D}', label: 'Muy bonita' },
  { value: 'good', emoji: '\u{1F60A}', label: 'Bonita' },
  { value: 'okay', emoji: '\u{1F642}', label: 'Normal' },
  { value: 'not_great', emoji: '\u{1F614}', label: 'No tanto' },
];

const illustrationOptions = [
  { value: 'loved', emoji: '\u{1F929}', label: 'Le encantaron' },
  { value: 'liked', emoji: '\u{1F60A}', label: 'Le gustaron' },
  { value: 'not_much', emoji: '\u{1F615}', label: 'No mucho' },
];

const whenOptions = [
  { value: 'today', label: 'Hoy mismo', sublabel: 'Quiero ver más historias ahora' },
  { value: 'tomorrow', label: 'Mañana', sublabel: 'Nos encantaría seguir pronto' },
  { value: 'next_week', label: 'La otra semana', sublabel: 'Cuando tengamos un ratito juntos' },
  { value: 'never', label: 'Preferiría no volver a leer uno', sublabel: '' },
];

const StepIndicator = ({ current, total }) => (
  <div className="flex gap-1.5 justify-center mb-6">
    {Array.from({ length: total }, (_, i) => (
      <div
        key={i}
        className={`h-1 rounded-full transition-all duration-500 ${
          i <= current ? 'bg-foreground w-6' : 'bg-foreground/10 w-4'
        }`}
      />
    ))}
  </div>
);

const SlideWrapper = ({ children, stepKey }) => (
  <motion.div
    key={stepKey}
    initial={{ opacity: 0, y: 24, filter: 'blur(6px)' }}
    animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
    exit={{ opacity: 0, y: -16, filter: 'blur(4px)' }}
    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
    className="w-full"
  >
    {children}
  </motion.div>
);

const GiftOnboarding = ({ book, onClose }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [experience, setExperience] = useState('');
  const [illustrations, setIllustrations] = useState('');
  const [whenAgain, setWhenAgain] = useState('');
  const [neverReason, setNeverReason] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [addedToCartCount, setAddedToCartCount] = useState(0);

  useEffect(() => {
    authFetch(`${API}/books/suggestions`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setSuggestions(
        (data || []).map(b => ({
          ...b,
          coverUrl: `${R2_URL}/books/${b.imageFolder}/cover.webp`,
        }))
      ))
      .catch(() => {});
  }, []);

  const submitSurvey = async (when) => {
    if (submitted) return;
    setSubmitting(true);
    try {
      await authFetch(`${API}/surveys/gift`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          book_slug: book.slug,
          experience,
          illustrations,
          when_again: when,
          never_reason: when === 'never' ? neverReason : '',
        }),
      });
      setSubmitted(true);
    } catch {}
    setSubmitting(false);
  };

  const addToCart = async (slug) => {
    await authFetch(`${API}/user/cart/${slug}`, { method: 'POST' });
    updateCartBadge();
    setAddedToCartCount(prev => prev + 1);
    toast.success('Agregado al carrito');
  };

  const canClose = !(step === 4 && whenAgain === 'never' && neverReason.trim().length < 5);

  // Total visual steps: 0=welcome, 1=experience, 2=illustrations, 3=when, 4=result
  const totalSteps = 5;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className="bg-background rounded-2xl shadow-2xl w-[90%] max-w-md max-h-[85vh] overflow-y-auto p-6 relative"
        onClick={e => e.stopPropagation()}
      >
        {/* Back button - show on steps 1-4 to go back */}
        {step > 0 && (
          <button
            onClick={() => {
              if (step === 4) { setWhenAgain(''); setNeverReason(''); setSubmitted(false); setStep(3); }
              else setStep(step - 1);
            }}
            className="absolute top-4 left-4 text-muted-foreground/40 hover:text-foreground transition-colors p-1"
            data-testid="gift-onboarding-back"
          >
            <svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          </button>
        )}

        <StepIndicator current={step} total={totalSteps} />

        <AnimatePresence mode="wait">
          {/* Step 0: Welcome */}
          {step === 0 && (
            <SlideWrapper stepKey="welcome">
              <div className="text-center py-4">
                <motion.div
                  className="text-5xl mb-5"
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity, repeatType: 'reverse' }}
                >
                  {'\u2728'}
                </motion.div>
                <h2 className="text-lg font-bold mb-2">
                  ¡Qué bonito fue leer juntos!
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                  Queremos saber cómo fue la experiencia de leer <strong>{book.title}</strong> con tu peque. Son solo 3 preguntitas rápidas.
                </p>
                <button
                  data-testid="gift-onboarding-start"
                  onClick={() => setStep(1)}
                  className="h-11 px-8 rounded-xl bg-foreground text-background text-sm font-semibold hover:bg-foreground/90 transition-colors"
                >
                  Empezar
                </button>
              </div>
            </SlideWrapper>
          )}

          {/* Step 1: Experience */}
          {step === 1 && (
            <SlideWrapper stepKey="experience">
              <div className="text-center py-2">
                <h2 className="text-base font-bold mb-1">
                  ¿Cómo fue la experiencia de leer con tu peque?
                </h2>
                <p className="text-xs text-muted-foreground mb-5">
                  Tu opinión nos ayuda a crear mejores historias
                </p>
                <div className="flex justify-center gap-3 mb-6">
                  {experienceOptions.map(opt => (
                    <button
                      key={opt.value}
                      data-testid={`exp-${opt.value}`}
                      onClick={() => { setExperience(opt.value); setTimeout(() => setStep(2), 400); }}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-xl transition-all duration-200 ${
                        experience === opt.value
                          ? 'bg-foreground/5 scale-110 ring-2 ring-foreground/20'
                          : 'hover:bg-secondary/50'
                      }`}
                    >
                      <span className="text-2xl">{opt.emoji}</span>
                      <span className="text-[10px] text-muted-foreground font-medium">{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </SlideWrapper>
          )}

          {/* Step 2: Illustrations */}
          {step === 2 && (
            <SlideWrapper stepKey="illustrations">
              <div className="text-center py-2">
                <h2 className="text-base font-bold mb-1">
                  ¿Le gustaron las ilustraciones a tu peque?
                </h2>
                <p className="text-xs text-muted-foreground mb-5">
                  Cada ilustración está hecha con mucho cariño
                </p>
                <div className="flex justify-center gap-4 mb-6">
                  {illustrationOptions.map(opt => (
                    <button
                      key={opt.value}
                      data-testid={`illust-${opt.value}`}
                      onClick={() => { setIllustrations(opt.value); setTimeout(() => setStep(3), 400); }}
                      className={`flex flex-col items-center gap-2 p-4 rounded-2xl transition-all duration-200 ${
                        illustrations === opt.value
                          ? 'bg-foreground/5 scale-105 ring-2 ring-foreground/20'
                          : 'hover:bg-secondary/50 bg-secondary/30'
                      }`}
                    >
                      <span className="text-3xl">{opt.emoji}</span>
                      <span className="text-xs text-muted-foreground font-medium">{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </SlideWrapper>
          )}

          {/* Step 3: When again */}
          {step === 3 && (
            <SlideWrapper stepKey="when">
              <div className="py-2">
                <div className="text-center mb-5">
                  <h2 className="text-base font-bold mb-1">
                    ¿Cuándo les gustaría leer otra historia juntos?
                  </h2>
                  <p className="text-xs text-muted-foreground">
                    Cada librito es una nueva aventura de fe
                  </p>
                </div>
                <div className="space-y-2">
                  {whenOptions.map(opt => (
                    <button
                      key={opt.value}
                      data-testid={`when-${opt.value}`}
                      onClick={() => {
                        setWhenAgain(opt.value);
                        if (opt.value !== 'never') {
                          submitSurvey(opt.value);
                          setTimeout(() => setStep(4), 300);
                        } else {
                          setStep(4);
                        }
                      }}
                      className={`w-full text-left px-4 py-3.5 rounded-xl border transition-all duration-200 ${
                        whenAgain === opt.value
                          ? 'border-foreground/30 bg-foreground/5'
                          : 'border-border hover:border-foreground/20 hover:bg-secondary/30'
                      }`}
                    >
                      <p className="text-sm font-medium">{opt.label}</p>
                      {opt.sublabel && <p className="text-[11px] text-muted-foreground mt-0.5">{opt.sublabel}</p>}
                    </button>
                  ))}
                </div>
              </div>
            </SlideWrapper>
          )}

          {/* Step 4: Result */}
          {step === 4 && (
            <SlideWrapper stepKey="result">
              {/* TODAY → show books */}
              {whenAgain === 'today' && (
                <div className="py-2 text-center">
                  <motion.div
                    className="text-4xl mb-3"
                    animate={{ rotate: [0, 10, -10, 0] }}
                    transition={{ duration: 1.5, repeat: 2 }}
                  >
                    {'\u{1F389}'}
                  </motion.div>
                  <h2 className="text-base font-bold mb-1">¡Eso nos encanta!</h2>
                  <p className="text-xs text-muted-foreground mb-5">
                    Aquí tienes algunas historias que a tu peque le van a fascinar
                  </p>
                  {suggestions.length > 0 ? (
                    <div className="space-y-3 mb-5">
                      {suggestions.map(s => (
                        <div key={s.slug} className="flex items-center gap-3 p-3 rounded-xl bg-secondary/30 text-left">
                          <div className="w-12 h-16 rounded-lg overflow-hidden shrink-0 shadow-sm">
                            <BookCover book={s} hideTitle />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{s.title}</p>
                            <p className="text-xs text-emerald-600 font-semibold">${s.price?.toFixed(2)} USD</p>
                          </div>
                          <button
                            data-testid={`suggest-cart-${s.slug}`}
                            onClick={() => addToCart(s.slug)}
                            className="shrink-0 size-9 rounded-full bg-foreground text-background flex items-center justify-center hover:bg-foreground/90 transition-colors"
                          >
                            <ShoppingCart className="size-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground mb-4">Cargando sugerencias...</p>
                  )}
                  <div className="flex flex-col gap-2">
                    {addedToCartCount > 0 && (
                      <button
                        data-testid="gift-onboarding-go-cart"
                        onClick={() => { onClose(); navigate('/carrito'); }}
                        className="w-full h-11 rounded-xl bg-foreground text-background text-sm font-semibold hover:bg-foreground/90 transition-colors flex items-center justify-center gap-2"
                      >
                        <ShoppingCart className="size-4" /> Ir al carrito
                      </button>
                    )}
                    <button
                      data-testid="gift-onboarding-explore"
                      onClick={() => { onClose(); navigate('/'); }}
                      className="w-full h-11 rounded-xl border border-border text-muted-foreground text-sm font-medium hover:text-foreground hover:bg-secondary/50 transition-colors flex items-center justify-center gap-2"
                    >
                      Ver más historias <ArrowRight className="size-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* TOMORROW or NEXT WEEK → warm message */}
              {(whenAgain === 'tomorrow' || whenAgain === 'next_week') && (
                <div className="py-4 text-center">
                  <motion.div
                    className="text-4xl mb-3"
                    animate={{ y: [0, -6, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    {'\u{1F4E8}'}
                  </motion.div>
                  <h2 className="text-base font-bold mb-2">¡Perfecto, los esperamos!</h2>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-2">
                    {whenAgain === 'tomorrow'
                      ? 'Mañana te enviaremos un recordatorio con algunas historias que a tu peque le van a encantar.'
                      : 'La otra semana te enviaremos unas sugerencias hermosas para que sigan leyendo juntos.'}
                  </p>
                  <p className="text-xs text-muted-foreground/50 mb-6">
                    Cada historia es una semilla de fe que queda en su corazoncito
                  </p>
                  <button
                    data-testid="gift-onboarding-done"
                    onClick={onClose}
                    className="h-11 px-8 rounded-xl bg-foreground text-background text-sm font-semibold hover:bg-foreground/90 transition-colors"
                  >
                    Listo, gracias
                  </button>
                </div>
              )}

              {/* NEVER → required reason */}
              {whenAgain === 'never' && (
                <div className="py-2 text-center">
                  <h2 className="text-base font-bold mb-2">Entendemos, ¿nos cuentas por qué?</h2>
                  <p className="text-xs text-muted-foreground mb-4">
                    Tu opinión nos ayuda muchísimo a mejorar. No te tomará más de un minuto.
                  </p>
                  <textarea
                    data-testid="never-reason-input"
                    value={neverReason}
                    onChange={e => setNeverReason(e.target.value)}
                    maxLength={300}
                    placeholder="Cuéntanos qué podríamos mejorar..."
                    className="w-full h-24 px-4 py-3 rounded-xl bg-secondary border border-border text-sm outline-none focus:ring-2 focus:ring-foreground/20 resize-none mb-1"
                  />
                  <p className="text-[10px] text-muted-foreground/40 mb-4 text-right">{neverReason.length}/300</p>
                  <button
                    data-testid="never-reason-submit"
                    disabled={neverReason.trim().length < 5 || submitting}
                    onClick={async () => {
                      await submitSurvey('never');
                      onClose();
                    }}
                    className="w-full h-11 rounded-xl bg-foreground text-background text-sm font-semibold hover:bg-foreground/90 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Enviando...' : 'Enviar y cerrar'}
                  </button>
                </div>
              )}
            </SlideWrapper>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default GiftOnboarding;
